import React, { useState } from 'react';
import { withTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import axios from 'axios';
import { useUserStore } from 'store';
import { getCsrfToken, formatQuantity } from '../../utils';
import DecimalField from '../shared/DecimalField';

function StockAdjustDialog({ t, open, onClose, inventoryItem, onSuccess }) {
  const { currentOrganization } = useUserStore();
  const [adjustmentType, setAdjustmentType] = useState('add');
  const [quantity, setQuantity] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = () => {
    setAdjustmentType('add');
    setQuantity('');
    setNotes('');
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!quantity || parseFloat(quantity) <= 0) {
      setError(t('Please enter a valid quantity'));
      return;
    }

    if (!notes.trim()) {
      setError(t('inventory.notesRequired'));
      return;
    }

    const quantityChange = adjustmentType === 'add'
      ? parseFloat(quantity)
      : -parseFloat(quantity);

    try {
      setLoading(true);
      setError(null);

      await axios.post(
        `/api/inventory/${inventoryItem.id}/adjust/?organization=${currentOrganization.organization_id}`,
        {
          quantity_change: quantityChange.toString(),
          notes: notes,
        },
        {
          headers: {
            'X-CSRFToken': getCsrfToken(),
          },
        }
      );

      onSuccess?.();
      handleClose();
    } catch (err) {
      console.error('Failed to adjust stock:', err);
      setError(err.response?.data?.error || t('Failed to adjust stock'));
    } finally {
      setLoading(false);
    }
  };

  if (!inventoryItem) return null;

  const currentStock = parseFloat(inventoryItem.quantity_in_stock) || 0;
  const adjustmentAmount = parseFloat(quantity) || 0;
  const newStock = adjustmentType === 'add'
    ? currentStock + adjustmentAmount
    : currentStock - adjustmentAmount;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth data-testid="stock-adjust-dialog">
      <DialogTitle>{t('inventory.adjustStock')}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {inventoryItem.part_name}
          </Typography>

          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('inventory.systemQuantity')}: <strong>{formatQuantity(inventoryItem.quantity_in_stock)}</strong>
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mb: 2 }}>
            <ToggleButtonGroup
              value={adjustmentType}
              exclusive
              onChange={(e, val) => val && setAdjustmentType(val)}
              fullWidth
              size="small"
            >
              <ToggleButton value="add" color="success">
                <AddIcon sx={{ mr: 0.5 }} />
                {t('Add')}
              </ToggleButton>
              <ToggleButton value="remove" color="error">
                <RemoveIcon sx={{ mr: 0.5 }} />
                {t('Remove')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <DecimalField
            label={t('inventory.quantity')}
            value={quantity}
            onChange={setQuantity}
            required
            fullWidth
            sx={{ mb: 2 }}
          />

          {quantity && (
            <Alert
              severity={newStock < 0 ? 'warning' : 'info'}
              sx={{ mb: 2 }}
            >
              {t('New quantity')}: <strong>{formatQuantity(newStock)}</strong>
              {newStock < 0 && ` (${t('inventory.negativeStock')})`}
            </Alert>
          )}

          <TextField
            label={t('inventory.reason')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={2}
            required
            helperText={t('inventory.notesRequired')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('Cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color={adjustmentType === 'add' ? 'success' : 'error'}
          disabled={loading || !quantity || !notes.trim()}
          data-testid="stock-adjust-submit"
        >
          {loading ? <CircularProgress size={20} /> : t('inventory.adjustStock')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default withTranslation()(StockAdjustDialog);
